import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { STRINGS, localize } from "@/lib/i18n";
import { api } from "@/lib/api";

const LanguageContext = createContext(null);
const SUPPORTED = ["en", "cs", "he", "ru"];

function detectInitialLang() {
  const saved = localStorage.getItem("cph_lang");
  if (saved && SUPPORTED.includes(saved)) return saved;
  const cachedDefault = localStorage.getItem("cph_default_lang");
  if (cachedDefault && SUPPORTED.includes(cachedDefault)) return cachedDefault;
  const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  if (nav.startsWith("he") || nav.startsWith("iw")) return "he";
  if (nav.startsWith("ru")) return "ru";
  if (nav.startsWith("cs")) return "cs";
  return "en";
}

// Built-in string lookup. A key that exists with an empty value means "intentionally
// blank" (e.g. an optional hero subtitle) — return "" instead of leaking the key name.
function resolveBuiltin(lg, key) {
  const own = STRINGS[lg] && STRINGS[lg][key];
  if (own) return own;
  const en = STRINGS.en[key];
  if (en) return en;
  if ((STRINGS[lg] && Object.prototype.hasOwnProperty.call(STRINGS[lg], key)) || Object.prototype.hasOwnProperty.call(STRINGS.en, key)) return "";
  return key;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const [overrides, setOverrides] = useState({ en: {}, cs: {}, he: {}, ru: {} });
  const [enabledLangs, setEnabledLangs] = useState(SUPPORTED);

  const refreshOverrides = useCallback(() => {
    return api.get("/site-text")
      .then((r) => { setOverrides({ en: r.data.en || {}, cs: r.data.cs || {}, he: r.data.he || {}, ru: r.data.ru || {} }); return r.data; })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshOverrides();
    // Admin-configured default language (only applies when the visitor hasn't picked one)
    api.get("/settings")
      .then((r) => {
        const dflt = r.data.default_lang;
        // Languages the admin has made visible on the public site (English always on).
        let enabled = Array.isArray(r.data.enabled_languages) && r.data.enabled_languages.length
          ? r.data.enabled_languages.filter((l) => SUPPORTED.includes(l))
          : [...SUPPORTED];
        if (!enabled.includes("en")) enabled = ["en", ...enabled];
        setEnabledLangs(enabled);
        if (dflt && SUPPORTED.includes(dflt)) localStorage.setItem("cph_default_lang", dflt);
        const fallback = dflt && enabled.includes(dflt) ? dflt : "en";
        setLangState((cur) => {
          const saved = localStorage.getItem("cph_lang");
          // Visitor's saved choice is no longer offered → fall back to default.
          if (saved && !enabled.includes(cur)) return fallback;
          // New visitor with no saved choice → open in the admin default.
          if (!saved && dflt && enabled.includes(dflt)) return dflt;
          return cur;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", lang === "he" ? "rtl" : "ltr");
  }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("cph_lang", l);
  }, []);

  const t = useCallback(
    (key) => {
      const ov = overrides[lang];
      // Respect an explicitly-set override even when it's blank ("") — admin
      // only stores keys they intentionally changed, so a blank means "hide it".
      if (ov && Object.prototype.hasOwnProperty.call(ov, key)) return ov[key];
      return resolveBuiltin(lang, key);
    },
    [lang, overrides]
  );

  const tl = useCallback((item, field) => localize(item, field, lang), [lang]);

  // Resolve a fixed-text key for a SPECIFIC language (used by inline edit mode).
  const tForLang = useCallback(
    (lg, key) => {
      const ov = overrides[lg];
      if (ov && Object.prototype.hasOwnProperty.call(ov, key)) return ov[key];
      return resolveBuiltin(lg, key);
    },
    [overrides]
  );

  // Returns an explicit Site Text override for a key (even blank ""), else undefined.
  const getOverride = useCallback(
    (key) => {
      const ov = overrides[lang];
      return ov && Object.prototype.hasOwnProperty.call(ov, key) ? ov[key] : undefined;
    },
    [lang, overrides]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tl, tForLang, getOverride, overrides, refreshOverrides, enabledLangs, isRTL: lang === "he" }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
